import { BankVault } from '../../../shared/CharacterTypes';
import { InventorySystem } from './InventorySystem';
import { gameLogger } from '../../../utils/GameLogger';
import { SystemBase } from '../SystemBase';

export class BankSystem extends SystemBase {
  public readonly id = 'BankSystem';
  private static instance: BankSystem | null = null;
  
  private vault: BankVault = {
    items: [],
    maxSlots: 100,
    gold: 0
  };

  private inventorySystem: InventorySystem;

  constructor(inventorySystem: InventorySystem) {
    super();
    this.inventorySystem = inventorySystem;
    BankSystem.instance = this;
  }

  public static getInstance(): BankSystem | null {
    return BankSystem.instance;
  }

  public getVault(): BankVault {
    return this.vault;
  }

  public depositGold(amount: number): boolean {
    if (this.inventorySystem.removeGold(amount)) {
      this.vault.gold += amount;
      gameLogger.info('BankSystem', `Deposited ${amount} gold. Bank balance: ${this.vault.gold}`);
      return true;
    }
    return false;
  }

  public withdrawGold(amount: number): boolean {
    if (this.vault.gold >= amount) {
      this.vault.gold -= amount;
      this.inventorySystem.addGold(amount);
      gameLogger.info('BankSystem', `Withdrew ${amount} gold. Bank balance: ${this.vault.gold}`);
      return true;
    }
    return false;
  }

  public depositItem(itemId: string, quantity: number = 1): boolean {
    const item = this.inventorySystem.findItem(itemId);
    if (!item) return false;

    // Check bank space (simplified - assumes no stacking logic for now, or just pushes)
    if (this.vault.items.length >= this.vault.maxSlots) {
      gameLogger.warn('BankSystem', 'Bank is full');
      return false;
    }

    if (this.inventorySystem.removeItem(itemId, quantity)) {
      // Very simple stacking for bank
      const existing = this.vault.items.find(i => i.id === itemId);
      if (existing && existing.maxStack > 1 && existing.quantity < existing.maxStack) {
         existing.quantity += quantity;
      } else {
         this.vault.items.push({ ...item, quantity });
      }
      return true;
    }
    return false;
  }

  public withdrawItem(itemId: string, quantity: number = 1): boolean {
    const index = this.vault.items.findIndex(i => i.id === itemId);
    if (index === -1) return false;

    const item = this.vault.items[index];
    if (item.quantity < quantity) return false;

    // Check inventory space
    if (this.inventorySystem.getEmptySlots() <= 0) {
      gameLogger.warn('BankSystem', 'Inventory is full, cannot withdraw');
      return false;
    }

    item.quantity -= quantity;
    if (item.quantity <= 0) {
      this.vault.items.splice(index, 1);
    }

    this.inventorySystem.addItem({ ...item, quantity }, quantity);
    return true;
  }

  public load(vaultData: BankVault): void {
    this.vault = {
      items: [...vaultData.items],
      maxSlots: vaultData.maxSlots || 100,
      gold: vaultData.gold || 0
    };
  }

  public save(): BankVault {
    return {
      items: [...this.vault.items],
      maxSlots: this.vault.maxSlots,
      gold: this.vault.gold
    };
  }
}
